import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import StatusDot from "@/components/common/StatusDot";
import { toneSurface } from "@/lib/mockData";
import { cn } from "@/lib/utils";

/**
 * AgentCard – one boardroom seat.
 * Tall (1:1.7), 28px radius, gradient surface per agent tone.
 * Subtle hover lift + glow pulse when the agent is the active speaker.
 */
export default function AgentCard({ agent, isActive = false, index = 0, isEnabled = true }) {
  const tone = toneSurface[agent.tone] ?? toneSurface.neutral;
  const isDark = agent.tone === "dark";
  const dynamicStatus = isActive ? "debating" : isEnabled ? "idle" : "disabled";

  return (
    <motion.article
      data-testid={`agent-card-${agent.id}`}
      data-active={isActive ? "true" : "false"}
      data-status={dynamicStatus}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.45,
        delay: 0.04 * index,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -2 }}
      className={cn(
        "relative flex aspect-[1/1.45] flex-col overflow-hidden rounded-[28px] border p-6",
        tone.bg,
        tone.text,
        tone.border,
        "transition-shadow duration-300 hover:shadow-[0_24px_48px_-24px_rgba(15,15,25,0.18)]",
        "card-soft",
        !isEnabled && "opacity-50 grayscale",
      )}
      style={
        isDark
          ? { boxShadow: "0 24px 48px -24px rgba(0,0,0,0.55)" }
          : undefined
      }
    >
      {/* Speaker glow */}
      {isActive && (
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.0, 0.7, 0.0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="pointer-events-none absolute -inset-px rounded-[28px]"
          style={{
            boxShadow:
              "0 0 0 2px rgba(124,58,237,0.45), 0 0 36px 6px rgba(124,58,237,0.25)",
          }}
        />
      )}

      {/* Top row: role + status */}
      <div className="flex items-start justify-between">
        <div
          className={cn("eyebrow", isDark ? "text-white/55" : "text-zinc-500")}
        >
          {agent.role}
        </div>
        <StatusDot status={dynamicStatus === "debating" ? "debating" : agent.status} variant={isDark ? "dark" : "light"} />
      </div>

      {/* Name */}
      <div className="mt-4">
        <h3 className="text-[34px] font-extrabold leading-none tracking-tight">
          {agent.name}
        </h3>
        <p className={cn("mt-2 text-[12.5px]", tone.subtle)}>{agent.focus}</p>
      </div>

      {/* Quote / preview */}
      <div className="mt-5 flex-1">
        <p
          className={cn(
            "line-clamp-5 text-[13.5px] leading-[1.55]",
            tone.quote,
          )}
        >
          <span className={cn(isDark ? "text-white/40" : "text-zinc-400")}>
            “
          </span>
          {agent.preview}
          <span className={cn(isDark ? "text-white/40" : "text-zinc-400")}>
            ”
          </span>
        </p>
      </div>

      {/* Footer link */}
      <div className="mt-5">
        <Link
          to={`/agents?id=${agent.id}`}
          data-testid={`agent-card-${agent.id}-open-chat`}
          className={cn(
            "group inline-flex items-center gap-1 text-[12.5px] font-semibold transition-colors",
            tone.link,
          )}
        >
          <span className="border-b border-current/0 transition-colors group-hover:border-current/40">
            Open 1:1 chat
          </span>
          <ArrowUpRight
            className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            strokeWidth={2.4}
          />
        </Link>
      </div>
    </motion.article>
  );
}
